import { Box } from '@optiaxiom/react';
import { IconCheck, IconCopy } from '@optiaxiom/icons';
import { useCopyToClipboard } from '@/lib/useCopyToClipboard';
import styles from './Terminal.module.css';

export interface TerminalLine {
  text: string;
  /** Render as a muted comment line. */
  comment?: boolean;
}

interface TerminalProps {
  lines: TerminalLine[];
  /** Header label shown next to the window dots. */
  title?: string;
  /** Accessible label for the copy button. */
  copyAriaLabel?: string;
}

/**
 * Dark terminal/code block with macOS-style chrome (window dots, a title, and a Copy
 * button) and numbered lines. Comment lines render muted. Copy grabs every line joined
 * by newlines. Reused by the API-key quickstart and the app-registered scaffold step.
 */
export function Terminal({ lines, title = 'Terminal', copyAriaLabel = 'Copy commands' }: TerminalProps) {
  const { copied, copy } = useCopyToClipboard();
  const copyText = lines.map((line) => line.text).join('\n');

  return (
    <Box className={styles.terminal}>
      <div className={styles.terminalHeader}>
        <span className={styles.dots}>
          <span className={`${styles.dot} ${styles.dotRed}`} />
          <span className={`${styles.dot} ${styles.dotYellow}`} />
          <span className={`${styles.dot} ${styles.dotGreen}`} />
        </span>
        <span className={styles.terminalTitle}>{title}</span>
        <button
          type="button"
          className={styles.copyButton}
          onClick={() => void copy(copyText)}
          aria-label={copyAriaLabel}
        >
          {copied ? <IconCheck /> : <IconCopy />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className={styles.terminalBody}>
        {lines.map((line, index) => (
          <div className={styles.line} key={index}>
            <span className={styles.lineNum}>{index + 1}</span>
            <span className={line.comment ? styles.comment : undefined}>{line.text}</span>
          </div>
        ))}
      </div>
    </Box>
  );
}
